import { useNavigate } from 'react-router-dom';
import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { KID_AVATARS } from '../../utils/constants';
import { KID_THEME_LIST } from '../../utils/kidThemes';
import {
  formatCurrency,
  parseCurrency,
} from '../../utils/currency';
import { dbKidData as db } from '../../services/supabase';
import KidDashboardTour from './KidDashboardTour';
import Button from '../UI/Button';
import Input from '../UI/Input';
import MyPiggyIcon from '../UI/MyPiggyIcon';
import '../../styles/brand-typography.css';
import './KidSetup.css';

const TOUR_KEYS = new Set([
  'tour_balance',
  'tour_add_money',
  'tour_spent_money',
  'tour_chores',
  'tour_savings',
  'tour_reports',
  'tour_settings',
]);

const STEP_PROGRESS = {
  avatar: 1,
  theme: 2,
  starting_money: 3,
  savings_goal: 4,
  personal_chore: 5,
};

const SetupShell = ({
  title,
  description,
  icon,
  step,
  children,
  onExit,
}) => (
  <div className="mypiggy-brand kid-setup fixed inset-0 z-[190] overflow-y-auto bg-slate-950/70 px-4 py-6">
    <div
      role="dialog"
      aria-modal="true"
      className="mx-auto w-full max-w-lg rounded-3xl bg-white p-4 sm:p-5 shadow-2xl"
    >
      {STEP_PROGRESS[step] && (
        <div className="mb-5">
          <div className="flex items-center justify-between text-xs font-bold text-purple-700">
            <span>Set up your Piggy Bank</span>
            <span>{STEP_PROGRESS[step]} of 5</span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-purple-100">
            <div
              className="h-full rounded-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all"
              style={{ width: `${STEP_PROGRESS[step] * 20}%` }}
            />
          </div>
        </div>
      )}

      {icon && <MyPiggyIcon name={icon} size="xl" className="mb-3" />}
      <h2 className="text-2xl font-extrabold text-gray-900">{title}</h2>
      {description && (
        <p className="mt-2 text-base font-bold leading-relaxed text-slate-600">{description}</p>
      )}

      <div className="mt-4">{children}</div>

      {onExit && (
        <button
          type="button"
          onClick={onExit}
          className="mt-5 w-full text-sm font-bold text-gray-500 hover:text-gray-800"
        >
          Finish Later
        </button>
      )}
    </div>
  </div>
);

export default function KidOnboarding({
  currentKid,
  currency = 'USD',
  refreshKid,
  onDashboardRefresh,
}) {
  const navigate = useNavigate();
  const initialStatus = currentKid?.onboarding_status || 'completed';
  const initialStep = currentKid?.onboarding_step || 'welcome';
  const [step, setStep] = useState(initialStep);
  const [showResume, setShowResume] = useState(initialStatus === 'in_progress');
  const [dismissed, setDismissed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [avatar, setAvatar] = useState(currentKid?.avatar || KID_AVATARS[0]);
  const [theme, setTheme] = useState(currentKid?.dashboard_theme || 'sunset');
  const [startingAmount, setStartingAmount] = useState('0.00');
  const [goal, setGoal] = useState({ name: '', amount: '' });
  const [chore, setChore] = useState({ title: '', amount: '' });
  const progressQueueRef = useRef(Promise.resolve());

  const active = initialStatus !== 'completed' && !dismissed;
  const tourOpen = active && !showResume && TOUR_KEYS.has(step);

  const saveProgress = useCallback(async (nextStep) => {
    await progressQueueRef.current.catch(() => undefined);
    await db.updateMyKidOnboarding('in_progress', nextStep);
    setStep(nextStep);
  }, []);

  const runAction = async (action) => {
    setSaving(true);
    setError('');
    try {
      await action();
    } catch (actionError) {
      setError(actionError?.message || 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleTourStepChange = useCallback((stepKey) => {
    setStep(stepKey);

    const saveRequest = progressQueueRef.current
      .catch(() => undefined)
      .then(() => db.updateMyKidOnboarding('in_progress', stepKey));

    progressQueueRef.current = saveRequest;

    saveRequest.catch((saveError) => {
      console.error('Unable to save kid tour progress:', saveError);
    });
  }, []);

  const selectedTheme = useMemo(
    () => KID_THEME_LIST.find((item) => item.id === theme),
    [theme]
  );

  if (!active) return null;

  if (showResume) {
    return (
      <SetupShell
        title="Welcome Back!"
        icon="my-piggy-bank"
        description="Let’s finish setting up your Piggy Bank. We remembered where you stopped."
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Button
            variant="secondary"
            onClick={() => {
              runAction(async () => {
                await saveProgress('tour_balance');
                setShowResume(false);
              });
            }}
          >
            Start Tour Again
          </Button>
          <Button onClick={() => setShowResume(false)}>
            Continue Setup
          </Button>
        </div>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="mt-4 w-full text-sm font-bold text-gray-500"
        >
          Finish Later
        </button>
      </SetupShell>
    );
  }

  if (step === 'welcome') {
    return (
      <SetupShell
        title={`Welcome to MyPiggy, ${currentKid?.nickname || 'friend'}!`}
        icon="kid-profile"
        description="MyPiggy helps you keep track of the real money in your piggy bank. You can see how much you have, record what you earn and spend, save for things you want, and complete chores."
      >
        <div className="rounded-2xl bg-purple-50 p-4 text-center">
          <MyPiggyIcon name="my-piggy-bank" size="xl" />
          <p className="mt-2 font-bold text-purple-900">It only takes a minute!</p>
        </div>
        <Button
          className="mt-5 w-full"
          onClick={() => runAction(() => saveProgress('tour_balance'))}
          disabled={saving}
        >
          {saving ? 'Starting...' : 'Set Up My Piggy Bank'}
        </Button>
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      </SetupShell>
    );
  }

  if (tourOpen) {
    return (
      <KidDashboardTour
        open
        initialStepKey={step}
        onStepChange={handleTourStepChange}
        onExit={() => setDismissed(true)}
        onFinish={() => runAction(() => saveProgress('avatar'))}
      />
    );
  }

  if (step === 'avatar') {
    return (
      <SetupShell
        step={step}
        title="Choose Your Avatar"
        description="Pick an emoji to show beside your name. You can change it anytime in Settings."
        onExit={() => setDismissed(true)}
      >
        <div className="grid grid-cols-4 gap-2 min-[460px]:grid-cols-5 sm:grid-cols-6">
          {KID_AVATARS.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setAvatar(item)}
              aria-label={`Choose ${item} avatar`}
              className={`flex min-h-12 items-center justify-center rounded-xl border-2 p-2 text-2xl transition ${
                avatar === item
                  ? 'border-purple-600 bg-purple-50 ring-2 ring-purple-200'
                  : 'border-gray-200 hover:border-purple-300'
              }`}
            >
              {item}
            </button>
          ))}
        </div>
        <Button
          className="mt-5 w-full"
          disabled={saving}
          onClick={() => runAction(async () => {
            await db.updateMyKidAvatar(avatar);
            await saveProgress('theme');
          })}
        >
          {saving ? 'Saving...' : avatar === currentKid?.avatar ? 'Keep This Avatar' : 'Use This Avatar'}
        </Button>
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      </SetupShell>
    );
  }

  if (step === 'theme') {
    return (
      <SetupShell
        step={step}
        title="Choose Your Favorite Theme"
        description="Pick the colors you like best. You can change them anytime in Settings."
        onExit={() => setDismissed(true)}
      >
        <div className="grid grid-cols-2 gap-3">
          {KID_THEME_LIST.map((item) => (
            <button
              key={item.id}
              type="button"
              aria-pressed={theme === item.id}
              onClick={() => setTheme(item.id)}
              className={`rounded-xl border-2 p-3 text-left ${
                theme === item.id ? 'border-gray-900 ring-2 ring-gray-200' : 'border-gray-200'
              }`}
            >
              <div className={`h-8 rounded-lg ${item.backgroundClass}`} />
              <div className="mt-2 font-bold text-gray-900">{item.emoji} {item.name}</div>
            </button>
          ))}
        </div>
        {selectedTheme && (
          <p className="mt-3 text-center text-sm text-gray-600">{selectedTheme.description}</p>
        )}
        <Button
          className="mt-5 w-full"
          disabled={saving}
          onClick={() => runAction(async () => {
            await db.updateDashboardTheme(theme);
            await saveProgress('starting_money');
          })}
        >
          {saving ? 'Saving...' : 'Use This Theme'}
        </Button>
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      </SetupShell>
    );
  }

  if (step === 'starting_money') {
    return (
      <SetupShell
        step={step}
        title="How Much Money Is in Your Piggy Bank?"
        description="Count the real money in your Piggy Bank and enter the total below."
        onExit={() => setDismissed(true)}
      >
        <Input
          label="Starting Amount"
          type="number"
          min="0"
          step="0.01"
          value={startingAmount}
          onChange={(event) => setStartingAmount(event.target.value)}
          required
        />
        <div className="mt-3 rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
          <strong>ⓘ</strong> Enter all the money you have before you start using MyPiggy. If your Piggy Bank is empty, leave it at {formatCurrency(0, currency)}.
        </div>
        <Button
          className="mt-5 w-full"
          disabled={saving || startingAmount === ''}
          onClick={() => runAction(async () => {
            const rawAmount = Number(startingAmount);
            if (!Number.isFinite(rawAmount) || rawAmount < 0) {
              throw new Error('Starting amount must be $0.00 or more.');
            }
            const amountCents = Math.round(rawAmount * 100);
            await db.setMyKidStartingMoney(amountCents);
            await saveProgress('savings_goal');
          })}
        >
          {saving ? 'Saving...' : 'Save My Starting Amount'}
        </Button>
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      </SetupShell>
    );
  }

  if (step === 'savings_goal') {
    return (
      <SetupShell
        step={step}
        title="Are You Saving for Something?"
        description="Create a savings goal for something you really want and watch yourself get closer to it."
        onExit={() => setDismissed(true)}
      >
        <div className="space-y-3">
          <Input
            label="What are you saving for?"
            value={goal.name}
            onChange={(event) => setGoal((value) => ({ ...value, name: event.target.value }))}
            placeholder="A game, bike, trip..."
          />
          <Input
            label="Goal Amount"
            type="number"
            min="0.01"
            step="0.01"
            value={goal.amount}
            onChange={(event) => setGoal((value) => ({ ...value, amount: event.target.value }))}
            placeholder="0.00"
          />
        </div>
        <div className="mt-5 grid grid-cols-2 gap-3">
          <Button
            variant="secondary"
            disabled={saving}
            onClick={() => runAction(() => saveProgress('personal_chore'))}
          >
            Skip for Now
          </Button>
          <Button
            disabled={saving || !goal.name.trim() || !goal.amount}
            onClick={() => runAction(async () => {
              const amountCents = parseCurrency(goal.amount);
              if (amountCents <= 0) throw new Error('Goal amount must be greater than $0.');
              await db.createSavingsGoal({
                name: goal.name.trim(),
                target_amount_cents: amountCents,
              });
              await saveProgress('personal_chore');
            })}
          >
            {saving ? 'Saving...' : 'Create Goal'}
          </Button>
        </div>
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      </SetupShell>
    );
  }

  if (step === 'personal_chore') {
    return (
      <SetupShell
        step={step}
        title="Do You Have Your Own Special Chore?"
        description="Your family already has chores you can choose from. You can also add a special chore you do at home or outside."
        onExit={() => setDismissed(true)}
      >
        <div className="space-y-3">
          <Input
            label="Chore Title"
            value={chore.title}
            onChange={(event) => setChore((value) => ({ ...value, title: event.target.value }))}
            placeholder="Help in the yard"
          />
          <Input
            label="How much is it worth?"
            type="number"
            min="0.01"
            step="0.01"
            value={chore.amount}
            onChange={(event) => setChore((value) => ({ ...value, amount: event.target.value }))}
            placeholder="0.00"
          />
        </div>
        <div className="mt-5 grid grid-cols-2 gap-3">
          <Button
            variant="secondary"
            disabled={saving}
            onClick={() => runAction(() => saveProgress('complete'))}
          >
            Skip for Now
          </Button>
          <Button
            disabled={saving || chore.title.trim().length < 2 || !chore.amount}
            onClick={() => runAction(async () => {
              const valueCents = parseCurrency(chore.amount);
              if (valueCents <= 0) throw new Error('Chore value must be greater than $0.');
              await db.createPersonalChore({
                family_id: currentKid.family_id,
                title: chore.title.trim(),
                value_cents: valueCents,
                is_active: true,
                created_by: 'child',
                created_by_child_id: currentKid.id,
                assigned_child_id: currentKid.id,
                source: 'child',
              });
              await saveProgress('complete');
            })}
          >
            {saving ? 'Saving...' : 'Save Chore'}
          </Button>
        </div>
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      </SetupShell>
    );
  }

  return (
    <SetupShell
      title="Your Piggy Bank Is Ready!"
      icon="celebration-achievement"
      description="You’re ready to track your money, complete chores, and save for things you want."
    >
      <div className="mb-4 rounded-xl border border-teal-200 bg-teal-50 p-3 text-sm font-bold text-teal-900">You can find MyPiggy Kids and Parents apps in Settings. Look for MyPiggy Apps.</div>
      <Button
        className="w-full"
        disabled={saving}
        onClick={() => runAction(async () => {
          await db.updateMyKidOnboarding('completed', 'complete');
          setDismissed(true);
          await Promise.all([
            refreshKid(),
            onDashboardRefresh?.(),
          ]);
        })}
      >
        {saving ? 'Finishing...' : 'Go to My Dashboard'}
      </Button>
      <Button variant="secondary" className="mt-3 w-full" disabled={saving} onClick={() => runAction(async () => {
        await db.updateMyKidOnboarding('completed', 'complete');
        await Promise.all([refreshKid(), onDashboardRefresh?.()]);
        setDismissed(true);
        navigate('/kid/dashboard', { state: { viewKidApps: true } });
      })}>Finish &amp; View Apps</Button>
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
    </SetupShell>
  );
}

